import { Tag } from "@/components/elements/Tag";
import { lightBlue, red, text } from "@/theme/colors";
import { spacing } from "@/theme/variables";
import { APP_ENV, TAppEnv } from "@/types/enum";
import { Box } from "@mui/material";

type TRuntimeEnvTagStyle = {
  title: string;
  color: string;
  textColor: string;
};

// Only non-production-like environments that must stay visually obvious get a tag.
// DEV (and any unknown value) renders nothing.
const RUNTIME_ENV_TAG_STYLES: Partial<Record<TAppEnv, TRuntimeEnvTagStyle>> = {
  [APP_ENV.STG]: { title: APP_ENV.STG, color: lightBlue[80], textColor: text.white },
};

/**
 * Returns the tag style for the current runtime environment, or `undefined`
 * when the environment should not be badged (e.g. DEV / unset).
 */
export const getRuntimeEnvTagStyle = (): TRuntimeEnvTagStyle | undefined => {
  const appEnv = process.env.REACT_APP_APP_ENV as TAppEnv | undefined;

  return appEnv ? RUNTIME_ENV_TAG_STYLES[appEnv] : undefined;
};

/** Badge showing which runtime environment the app is served from (STG / PROD). */
export const RuntimeEnvTag = () => {
  const style = getRuntimeEnvTagStyle();

  if (!style) return null;

  return <Tag variant="bold" size="medium" minWidth="auto" {...style} style={{ borderRadius: 6 }} />;
};

/**
 * `RuntimeEnvTag` pinned to the top-right corner of its nearest positioned ancestor.
 * Used by the full-screen pages that have no AppHeader (auth layout, error pages),
 * so the caller only needs `position: relative`.
 */
export const RuntimeEnvTagCorner = () => {
  if (!getRuntimeEnvTagStyle()) return null;

  return (
    <Box position="absolute" top={spacing.sm} right={spacing.sm}>
      <RuntimeEnvTag />
    </Box>
  );
};
